import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/**
 * Digital Replica Licensing visibility gate.
 *
 * Two independent switches must BOTH be on:
 *  1. the global `digital_replica_licensing` row in `feature_flags`
 *  2. `profiles.is_beta_tester` on the signed-in account, or the platform admin role
 *
 * While either is off, no route, nav link, card or component from this
 * feature renders - including by direct URL.
 */
export function useDigitalReplicaAccess() {
  const { user, loading: authLoading } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      if (!user) {
        if (!cancelled) {
          setEnabled(false);
          setLoading(false);
        }
        return;
      }

      const [flagRes, profileRes, adminRes] = await Promise.all([
        supabase.from("feature_flags").select("enabled").eq("key", "digital_replica_licensing").maybeSingle(),
        supabase.from("profiles").select("is_beta_tester").eq("id", user.id).maybeSingle(),
        supabase.from("user_roles").select("role").eq("user_id", user.id).eq("role", "admin").maybeSingle(),
      ]);

      if (cancelled) return;
      setEnabled(
        Boolean(flagRes.data?.enabled) &&
          (Boolean(profileRes.data?.is_beta_tester) || Boolean(adminRes.data)),
      );
      setLoading(false);
    };

    if (!authLoading) check();
    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  return { enabled, loading: authLoading || loading };
}
